<template>
	<row-object-page name="campaign_edit" :oref="oref" v-model="campaign"></row-object-page>
	<ui-root v-if="showSetup" :part="setupForm" :data="setupData" :display="{ mode: 'modal', moveable:true, resizable:true }"
			 :errs="setupErrors" :actions="setupControls" :readonly="setupReadOnly"
			 @close="showSetup=false" ref="setupDialog"></ui-root>
</template>

<script setup lang="ts">
import {objectRef, RowObjectInit, RowObjectRef, RowObjectType, SrcType} from "@t/meta/SourceType.ts";
import RowObjectPage from "@/pages/row-object/RowObjectPage.vue";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import {computed, onUnmounted, reactive, ref} from "vue";
import {Campaign} from "@t/marketing/Campaign.ts";
import {NEW_RECS_GROUP_ID, SideMenu} from "@t/ui/Menu.ts";
import {hasAccess} from "@t/security/ObjectAccess.ts";
import {ErrorMap} from "@fd/PartInjectKeys.ts";
import {CommandHandlerResult} from "@fd/Types.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import {FormData, FormPart} from "@t/fd/FormPart.ts";
import {DataWithMeta, removeMeta, WithMeta} from "@t/fd/WithMeta.ts";
import {Flow} from "@t/fd/layout/Flow.ts";
import {Button} from "@t/fd/fixed/Button.ts";
import {AlwaysVisible} from "@t/fd/layout/AlwaysVisible.ts";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";
import {CREATE, EDIT, REPLACE_INIT_WITH_EDIT} from "@/pages/row-object/RowObjectEvents.ts";
import {standardNewRecordGroup} from "@/store/MenuBuilder.ts";
import {ApiError, ApiErrorResponse} from "@t/util/ApiError.ts";
import {useRouter} from "vue-router";


const ns = useNavigationStore();
const env = injectEnv();
const bus = connectEventBus("CampaignPage");
const campaign = ref<Campaign>(null);
const route = useVirtualRoute();
const router = useRouter();
const oref = objectRef(route, SrcType.CAMPAIGN);

// Setup
const showSetup = ref<boolean>(false);
const setupForm = ref<InterfaceRoot<FormDefinition>|null>(null);
const setupData = ref<FormData|null>(null);
const setupErrors:ErrorMap = reactive<ErrorMap>([]);
const setupReadOnly = computed<boolean>(() => {
	if (setupData.value) {
		return !hasAccess(setupData.value, "edit");
	}
	return true;
});
const setupControls = computed<FormPart|null>(() => {
	let buttons:Flow = { type: "flow", items:[], options: { style: "flex-wrap: wrap-reverse" } };
	buttons.items.push(<Button>{ type: "button", label: env.msg('bas.cancel'), action: "cancelWorkerSetup", options: { clazz: "sbutton" } });
	if (!setupReadOnly.value) {
		buttons.items.push(<Button>{ type: "button", mutating: true, label: env.msg('butt.save'), action: "saveWorkerSetup", feedback: true });
	}
	return <AlwaysVisible>{ type: "always-visible", body: buttons, options: { style: 'display: inline-block' } };
});
const setupDialog = ref(null);


ns.listenForStackerUpdates(bus, oref, () => env.msg('menu.createCampaign'),
	"/editCampaign.do", () => campaign.value.name, () => campaign.value.id);

ns.defineMenu((mb) => {
	if (campaign.value?.id) {
		mb.pageNamed("campaign_edit");
		mb.inGroup(NEW_RECS_GROUP_ID, standardNewRecordGroup());
	}
});

async function registerWorker():Promise<CommandHandlerResult> {
	let ep = "api/campaigns/"+campaign.value.id+"/worker/register/"+campaign.value.workerName;
	const nr = await env.net.fetch(ep, "POST");
	if (nr.ok) {
		campaign.value.workerRegistered = true;
		setTimeout(() => bus.fire("refresh:loadable", "worker-controls"), 500);
	}
	return nr.ok ? "ok" : "fail";
}

async function deregisterWorker():Promise<CommandHandlerResult> {
	let ep = "api/campaigns/"+campaign.value.id+"/worker/deregister";
	const nr = await env.net.fetch(ep, "POST");
	if (nr.ok) {
		campaign.value.workerName = "";
		campaign.value.workerRegistered = false;
		setTimeout(() => bus.fire("refresh:loadable", "worker-controls"), 500);
	}
	return nr.ok ? "ok" : "fail";
}

async function startWorker():Promise<CommandHandlerResult> {
	let ep = "api/campaigns/"+campaign.value.id+"/worker/start";
	campaign.value.workerOperating = true;
	const nr = await env.net.fetch(ep, "POST");
	if (nr.ok) {
		setTimeout(() => {
			console.log("firing refresh:loadable");
			bus.fire("refresh:loadable", "worker-controls");
			campaign.value.workerOperating = false;
		}, 500);
	} else campaign.value.workerOperating = false;
	return nr.ok ? "ok" : "fail";
}

async function stopWorker():Promise<CommandHandlerResult> {
	let ep = "api/campaigns/"+campaign.value.id+"/worker/stop";
	campaign.value.workerOperating = true;
	const nr = await env.net.fetch(ep, "POST");
	if (nr.ok) {
		setTimeout(() => {
			bus.fire("refresh:loadable", "worker-controls");
			campaign.value.workerOperating = false;
		}, 500);
	} else campaign.value.workerOperating = false;
	return nr.ok ? "ok" : "fail";
}

async function editWorkerSetup():Promise<CommandHandlerResult> {
	// Load data:
	let ep = "api/campaigns/"+campaign.value.id+"/worker/setup";
	const nr = await env.net.fetch(ep, "GET");
	if (nr.ok) {
		const dataMeta:DataWithMeta<FormData> = await nr.json();
		//console.log("dataMeta:"+JSON.stringify(dataMeta));
		setupForm.value = dataMeta.META.form;
		setupData.value = removeMeta(dataMeta);
		setupErrors.length = 0;
		showSetup.value = true;
	}
	return "ok";
}

async function cancelWorkerSetup():Promise<CommandHandlerResult> {
	showSetup.value = false;
	return "ok";
}

function processSetupProblems(problems:ApiError[]):void {
	if (!Array.isArray(problems)) return;
	setupErrors.push(...problems);
}

async function saveWorkerSetup():Promise<CommandHandlerResult> {
	let ep = "api/campaigns/"+campaign.value.id+"/worker/setup";
	const nr = await env.net.fetch(ep, "PUT", setupData.value);
	if (nr.ok) {
		showSetup.value = false;
		return "ok";
	} else {
		const er = await nr.json() as ApiErrorResponse;
		processSetupProblems(er.problems);
		return "fail";
	}
}

async function openAudience():Promise<CommandHandlerResult> {
	if (campaign.value.audience) {
		env.navigateTo(SrcType.TARGET_AUDIENCE, campaign.value.audience);
		return "ok";
	} else return "fail";
}

async function checkAudience():Promise<CommandHandlerResult> {
	let ep = "api/campaigns/"+campaign.value.id+"/check-now"
	const body = {
		audience: campaign.value.audience,
		add: !!campaign.value.autoAddAudience,
		finish: !!campaign.value.autoDoneAudience
	};
	const nr = await env.net.fetch(ep, "POST", body);
	if (nr.ok) {
		const response = await nr.json() as { lastAutoAdd: string };
		campaign.value.lastAutoAdd = response.lastAutoAdd;
		return "ok";
	}
	return "fail";
}

bus.onCommand(["stop-worker","start-worker","edit-worker-setup", "register-worker", "deregister-worker",
	"cancelWorkerSetup", "saveWorkerSetup", "openAudience", "checkAudience"], async (cmd:string):Promise<CommandHandlerResult> => {
	switch (cmd) {
		case "register-worker":
			return await registerWorker();
		case "deregister-worker":
			return await deregisterWorker();
		case "start-worker":
			return await startWorker();
		case "stop-worker":
			return await stopWorker();
		case "edit-worker-setup":
			return await editWorkerSetup();
		case "cancelWorkerSetup":
			return await cancelWorkerSetup();
		case "saveWorkerSetup":
			return await saveWorkerSetup();
		case "openAudience":
			return await openAudience();
		case "checkAudience":
			return await checkAudience();
	}
	return "pass";
});
</script>

<style>

</style>